import type {
  ConnectionPoint,
  Point,
  ShapeStyle,
  ShapeType,
  Size,
} from '../state/types';
import { buildEdgeMidpoints } from './connectionPoints';
import { Shape } from './Shape';

export class Ellipse extends Shape {
  readonly type: ShapeType = 'ellipse';

  constructor(
    position: Point,
    size: Size,
    style?: Partial<ShapeStyle>,
    text = ''
  ) {
    super(position, size, style, text);
  }

  containsPoint(p: Point): boolean {
    const center = this.getCenter();
    const rx = this.size.width / 2;
    const ry = this.size.height / 2;

    // Ellipse equation: (x-h)²/rx² + (y-k)²/ry² <= 1
    const dx = (p.x - center.x) / rx;
    const dy = (p.y - center.y) / ry;
    return dx * dx + dy * dy <= 1;
  }

  getConnectionPoints(): ConnectionPoint[] {
    const center = this.getCenter();
    const rx = this.size.width / 2;
    const ry = this.size.height / 2;
    // For diagonal points, use 45-degree angle on ellipse
    const diag = Math.SQRT1_2; // cos(45°) = sin(45°) = 1/√2
    const edgeMidpoints = buildEdgeMidpoints(this.id, this.position, this.size);

    return [
      ...edgeMidpoints,
      // Diagonal points (at 45° angles on ellipse)
      {
        id: `${this.id}-top-right`,
        position: { x: center.x + rx * diag, y: center.y - ry * diag },
        direction: 'top',
      },
      {
        id: `${this.id}-bottom-right`,
        position: { x: center.x + rx * diag, y: center.y + ry * diag },
        direction: 'bottom',
      },
      {
        id: `${this.id}-bottom-left`,
        position: { x: center.x - rx * diag, y: center.y + ry * diag },
        direction: 'bottom',
      },
      {
        id: `${this.id}-top-left`,
        position: { x: center.x - rx * diag, y: center.y - ry * diag },
        direction: 'top',
      },
    ];
  }

  render(ctx: CanvasRenderingContext2D): void {
    const center = this.getCenter();
    const rx = this.size.width / 2;
    const ry = this.size.height / 2;

    ctx.save();
    ctx.globalAlpha = this.style.opacity;

    ctx.beginPath();
    ctx.ellipse(center.x, center.y, rx, ry, 0, 0, Math.PI * 2);

    ctx.fillStyle = this.style.fill;
    ctx.fill();

    ctx.strokeStyle = this.style.stroke;
    ctx.lineWidth = this.style.strokeWidth;
    ctx.stroke();

    ctx.restore();

    this.renderText(ctx);
    this.renderSelection(ctx);
  }
}
